import {
  create,
  fromJson,
  toJson,
  type DescMessage,
  type JsonValue,
  type MessageShape,
} from '@bufbuild/protobuf';
import { apiClient } from './request';
import {
  CheckMaasCredentialNameRequestSchema,
  CheckMaasCredentialNameResponseSchema,
  CopyMaasCredentialKeyRequestSchema,
  CopyMaasCredentialKeyResponseSchema,
  CreateMaasCredentialRequestSchema,
  CreateMaasCredentialResponseSchema,
  DeleteMaasCredentialRequestSchema,
  DeleteMaasCredentialResponseSchema,
  GetMaasCredentialRequestSchema,
  GetMaasCredentialResponseSchema,
  ListMaasCredentialModelsRequestSchema,
  ListMaasCredentialModelsResponseSchema,
  ListMaasCredentialsResponseSchema,
  ListMaasModelsRequestSchema,
  ListMaasModelsResponseSchema,
  UpdateMaasCredentialRequestSchema,
  UpdateMaasCredentialResponseSchema,
  type CreateMaasCredentialRequest,
  type ListMaasCredentialModelsData,
  type ListMaasCredentialsData,
  type ListMaasModelsData,
  type UpdateMaasCredentialRequest,
} from '@/proto/agentplatform/httpv2/maas_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { MaasCredential } from '@/proto/agentplatform/v2/maas_pb';

/**
 * 拆 httpv2 成功包络取业务 data。envelope 的业务错误已由 apiClient 响应拦截器
 * 统一识别并 reject 成 ApiError（见 request.ts / errors.ts），这里只处理成功分支。
 */
async function send<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  const raw = responseObject.data;
  const wrapped = fromJson(successSchema, raw);
  const data = pickData(wrapped);
  if (data === undefined || data === null) {
    throw new Error('empty response data');
  }
  return data;
}

/** 空 envelope 的成功响应（Create/Update/Delete 无 data 字段）；只做格式校验。 */
async function sendVoid<TResponse extends DescMessage>(
  path: string,
  body: JsonValue,
  successSchema: TResponse
): Promise<void> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  fromJson(successSchema, responseObject.data); // 仅做格式校验
}

export const maasApi = {
  listCredentials: async (params: ListRequest): Promise<ListMaasCredentialsData> =>
    send(
      '/v2/ListMaasCredentials',
      toJson(ListRequestSchema, params),
      ListMaasCredentialsResponseSchema,
      (r) => r.data
    ),

  createCredential: async (data: CreateMaasCredentialRequest): Promise<void> =>
    sendVoid(
      '/v2/CreateMaasCredential',
      toJson(CreateMaasCredentialRequestSchema, data),
      CreateMaasCredentialResponseSchema
    ),

  getCredential: async (id: string): Promise<MaasCredential> =>
    send(
      '/v2/GetMaasCredential',
      toJson(GetMaasCredentialRequestSchema, create(GetMaasCredentialRequestSchema, { id })),
      GetMaasCredentialResponseSchema,
      (r) => r.data
    ),

  updateCredential: async (data: UpdateMaasCredentialRequest): Promise<void> =>
    sendVoid(
      '/v2/UpdateMaasCredential',
      toJson(UpdateMaasCredentialRequestSchema, data),
      UpdateMaasCredentialResponseSchema
    ),

  deleteCredential: async (id: string): Promise<void> =>
    sendVoid(
      '/v2/DeleteMaasCredential',
      toJson(DeleteMaasCredentialRequestSchema, create(DeleteMaasCredentialRequestSchema, { id })),
      DeleteMaasCredentialResponseSchema
    ),

  /** 按 api_key 探测远端可用模型；vendor 目前固定为 InfiniAI，服务端隐含处理。 */
  listAvailableModels: async (apiKey: string): Promise<ListMaasCredentialModelsData> =>
    send(
      '/v2/ListMaasCredentialModels',
      toJson(
        ListMaasCredentialModelsRequestSchema,
        create(ListMaasCredentialModelsRequestSchema, { apiKey })
      ),
      ListMaasCredentialModelsResponseSchema,
      (r) => r.data
    ),

  listModelsByCredential: async (id: string): Promise<ListMaasModelsData> =>
    send(
      '/v2/ListMaasModels',
      toJson(ListMaasModelsRequestSchema, create(ListMaasModelsRequestSchema, { id })),
      ListMaasModelsResponseSchema,
      (r) => r.data
    ),

  /** 复制凭证明文密钥（后端返回不脱敏值）。 */
  copyCredentialKey: async (id: string): Promise<string> =>
    send(
      '/v2/CopyMaasCredentialKey',
      toJson(
        CopyMaasCredentialKeyRequestSchema,
        create(CopyMaasCredentialKeyRequestSchema, { id })
      ),
      CopyMaasCredentialKeyResponseSchema,
      (r) => r.data
    ),

  /** 创建前预检：凭证名称全局是否可用。 */
  checkName: async (name: string): Promise<boolean> =>
    send(
      '/v2/CheckMaasCredentialName',
      toJson(
        CheckMaasCredentialNameRequestSchema,
        create(CheckMaasCredentialNameRequestSchema, { name })
      ),
      CheckMaasCredentialNameResponseSchema,
      (r) => r.data?.available
    ),
};
